/**
 * The Dashboard's global filter state and its URL persistence.
 *
 * Selections live in the URL query string so they survive a reload, are
 * shareable/deep-linkable, and — crucially — persist into the drill-through to
 * the Documents screen (a chart segment becomes a Documents filter in the same
 * param vocabulary). The param names are the shared, human-readable ones
 * (`type`, `location`, `scanner`, `source`, `start`, `end`); the backend's
 * `location_id` / `scanner_id` query names are applied only at the API edge by
 * {@link toApiFilter}.
 */
import type { AnalyticsFilter } from '../../api/resources';

export interface DashboardFilters {
  /** Document type (category name). */
  type?: string;
  /** Location id. */
  location?: string;
  /** Scanner id. */
  scanner?: string;
  /** Ingestion channel. */
  source?: string;
  /** Inclusive start day, `YYYY-MM-DD`. */
  start?: string;
  /** Inclusive end day, `YYYY-MM-DD`. */
  end?: string;
}

/** The categorical dimensions a chart can drill through on. */
export type DimensionKey = 'type' | 'location' | 'scanner' | 'source';

/** The dimension keys, in the order the filter bar renders them. */
export const DIMENSION_KEYS: readonly DimensionKey[] = ['type', 'location', 'scanner', 'source'];

/** All keys, in a stable order, so a serialised URL is deterministic. */
const ORDER: readonly (keyof DashboardFilters)[] = [
  'type',
  'location',
  'scanner',
  'source',
  'start',
  'end',
];

/** Read a filter set from a URL query string. Blank values are dropped. */
export function parseFilters(params: URLSearchParams): DashboardFilters {
  const out: DashboardFilters = {};
  for (const key of ORDER) {
    const value = params.get(key);
    if (value !== null && value !== '') out[key] = value;
  }
  return out;
}

/** Serialise a filter set to query params, omitting empty keys, stable order. */
export function serializeFilters(filters: DashboardFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of ORDER) {
    const value = filters[key];
    if (value !== undefined && value !== '') params.set(key, value);
  }
  return params;
}

/** Map a filter set to the analytics API's query shape (`*_id` for location/scanner). */
export function toApiFilter(filters: DashboardFilters): AnalyticsFilter {
  const out: AnalyticsFilter = {};
  if (filters.type !== undefined) out.type = filters.type;
  if (filters.location !== undefined) out.location_id = filters.location;
  if (filters.scanner !== undefined) out.scanner_id = filters.scanner;
  if (filters.source !== undefined) out.source = filters.source;
  if (filters.start !== undefined) out.start = filters.start;
  if (filters.end !== undefined) out.end = filters.end;
  return out;
}

/** True when any categorical dimension is set (the date range alone does not count). */
export function hasActiveFilters(filters: DashboardFilters): boolean {
  return DIMENSION_KEYS.some((key) => filters[key] !== undefined && filters[key] !== '');
}

/** Set one dimension to a single value, replacing any prior selection for it. */
export function withDimension(
  filters: DashboardFilters,
  key: DimensionKey,
  value: string | undefined,
): DashboardFilters {
  const next = { ...filters };
  next[key] = value === undefined || value === '' ? undefined : value;
  return next;
}

/**
 * Build the Documents URL for a drill-through: the currently-active filters plus
 * the clicked segment (which overrides any prior value for that dimension). The
 * date range rides along so the Documents view opens on the same window.
 */
export function drillToDocuments(
  filters: DashboardFilters,
  key: DimensionKey,
  value: string,
): string {
  const params = serializeFilters(withDimension(filters, key, value));
  return `/documents?${params.toString()}`;
}
